import React, { useState } from 'react';
import propTypes from 'prop-types';
import { Button } from 'antd';
import { Spacing } from 'works/legacy/lib/style-helper';
import { createMessage } from 'works/legacy/lib/message-helper';
import { OfferDataTable } from 'works/legacy/components/offer-data-table';
import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql, useMutation } from '@common/hooks';
import { Drawer, ErrorHandler, TextArea, Form } from '@common/components';
const CREATE_OFFER_CHANGE_REQUEST = gql`
  mutation CreateOfferChangeRequest(
    $changeRequestableId: ID!
    $changeRequestType: ChangeRequestType!
    $input: CreateOfferChangeRequestInput!
  ) {
    createOfferChangeRequest(
      changeRequestableId: $changeRequestableId
      changeRequestType: $changeRequestType
      input: $input
    ) {
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${FullValidationErrorFragment}
`;

export function OfferChangeRequestDrawer({
  application,
  offer,
  visible,
  type,
  onDrawerClose = () => {},
  onSuccess = () => {},
}) {
  const CHANGE_REQUEST_TEXT_MAX_LENGTH = 160;
  const [form] = Form.useForm();

  const [isFormValid, setIsFormValid] = useState(false);

  function hasFormAnyError(form) {
    const [fieldsErrors] = form.getFieldsError() || [];
    return fieldsErrors?.errors?.length > 0;
  }

  function onChange() {
    setTimeout(() => {
      const hasError = hasFormAnyError(form);
      setIsFormValid(!hasError);
    });
  }

  function onClose() {
    form.resetFields();
    setIsFormValid(false);
    onDrawerClose();
  }

  const [submitOfferChangeRequest, { error: error, loading }] = useMutation(
    CREATE_OFFER_CHANGE_REQUEST,
    {
      refetchQueries: [
        'SupplierJobPageCollectionQuery',
        'supplierDetailedCandidateProfile',
        'CandidateAssignmentQuery',
        'OfferConfirmationQuery',
      ],
      onCompleted: (response = {}) => {
        const { createOfferChangeRequest } = response;
        if (createOfferChangeRequest && !createOfferChangeRequest.errors) {
          createMessage('success', 'Change request has been submitted.');
          onSuccess();
        }

        onClose();
      },
      onError: () => {
        createMessage(
          'error',
          'Failed to submit change request. Please try again.'
        );
      },
    }
  );

  if (error) {
    return (
      <ErrorHandler
        error={error}
        messageType="contextual"
        log="OfferChangeRequestDrawer"
      />
    );
  }

  async function saveOfferChangeRequest({ offerChangeRequestMessage }) {
    submitOfferChangeRequest({
      variables: {
        changeRequestableId: offer.id,
        changeRequestType: type,
        input: {
          note: offerChangeRequestMessage,
        },
      },
    });
  }

  return (
    <>
      <Drawer
        className="offer-change-request-drawer"
        open={visible}
        title="Request Changes"
        onClose={onClose}
        footer={
          <div className="offer-change-request-drawer-footer">
            <Button
              key="cancelChangeRequestButton"
              size="large"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              key="submitChangeRequestButton"
              size="large"
              loading={loading}
              disabled={!isFormValid}
              onClick={() => form.submit()}
            >
              Submit Change Request
            </Button>
          </div>
        }
      >
        <OfferDataTable application={application} offer={offer} highlightMode />
        <Form
          layout="vertical"
          form={form}
          onFinish={saveOfferChangeRequest}
          onChange={onChange}
        >
          <Form.Item
            label="Change Request"
            name="offerChangeRequestMessage"
            rules={[
              {
                required: true,
                message: 'Please enter a change request',
              },
              {
                type: 'string',
                min: 1,
                max: CHANGE_REQUEST_TEXT_MAX_LENGTH,
                message: `Request must be fewer than ${CHANGE_REQUEST_TEXT_MAX_LENGTH} characters`,
              },
            ]}
          >
            <TextArea
              placeholder="What would you like changed?"
              name="offerChangeRequestMessage"
              maxLength={CHANGE_REQUEST_TEXT_MAX_LENGTH}
            />
          </Form.Item>
        </Form>
      </Drawer>
      <style jsx>{`
        :global(.offer-change-request-drawer-footer .ant-btn) {
          margin-right: ${Spacing.baseSpacing / 2}px;
        }
      `}</style>
    </>
  );
}

OfferChangeRequestDrawer.propTypes = {
  application: propTypes.object,
  offer: propTypes.object,
  visible: propTypes.bool,
  onDrawerClose: propTypes.func,
  onSuccess: propTypes.func,
  refetchQueries: propTypes.arrayOf(propTypes.string),
  type: propTypes.oneOf(['offer', 'extension_offer']).isRequired,
};
